import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import { CANDIDACY_PILL, pillFor } from '@/shared/utils/statusPills';
import StatusPill from '@/components/common/StatusPill';

/**
 * How the reviewer screens word a candidacy's stage. The server owns the
 * stage rules and sends each candidacy's allowed moves; this file holds only
 * the words and the display order.
 */
export const REVIEWER_STAGE = CANDIDACY_PILL;

// The server's list order, furthest along first. Display only: the server
// still orders the candidates inside each group. `unavailable`, which only
// faculty receive, is not stored and so is not here: a stage this list does
// not know is shown after these, which is where a closure belongs.
export const STAGE_GROUP_ORDER = Object.freeze([
    'accepted',
    'offered',
    'expired',
    'finalist',
    'interview',
    'screening',
    'applied',
    'released',
    'declined',
    'not_selected',
    'withdrawn',
    'closed',
]);

// The stages an offer may be made from: the client's copy of the server's
// OFFER_FROM (candidacyStages.js). The server still decides each candidacy's
// moves; this list only says where the screen explains that an interview is
// optional and which candidates an expired place can go to.
export const OFFER_FROM = Object.freeze([
    'applied',
    'screening',
    'interview',
    'finalist',
    'expired',
]);

// The stages a reviewer still works on: rank, interview, move, offer.
export const REVIEWABLE = Object.freeze([
    'applied',
    'screening',
    'interview',
    'finalist',
]);

// Steps a candidate may skip on the way to an offer.
export const OPTIONAL_STAGES = Object.freeze(['interview', 'finalist']);

// The hiring steps in order, as the stage legend lists them.
export const STAGE_STEPS = Object.freeze([
    'applied',
    'screening',
    'interview',
    'finalist',
    'offered',
    'accepted',
]);

export const stepLabelOf = (stage) =>
    OPTIONAL_STAGES.includes(stage)
        ? `${reviewerStageOf(stage).label} (optional)`
        : reviewerStageOf(stage).label;

export const OPTIONAL_INTERVIEW_TEXT =
    'Interviewing is optional. You can make an offer now, or move the student to Interview first.';

export const MOVE_LABEL = Object.freeze({
    screening: 'Mark as being reviewed',
    interview: 'Move to interview',
    finalist: 'Make a finalist',
    not_selected: 'Not selected',
    closed: 'No longer available',
});

// Moves that end the candidacy and email the student: the reviewer confirms.
export const CONFIRM_MOVES = Object.freeze(['not_selected', 'closed']);

// A stage this file does not know shows as itself rather than breaking the
// screen.
export function reviewerStageOf(stage) {
    return pillFor('candidacy', stage);
}

export function moveLabelOf(stage) {
    return MOVE_LABEL[stage] || `Move to ${reviewerStageOf(stage).label}`;
}

const TIME_ZONE = 'America/Denver';

export const formatDate = (value) =>
    new Date(value).toLocaleDateString('en-US', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });

export const formatDateTime = (value) =>
    new Date(value).toLocaleString('en-US', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });

// Short forms for a list row: the term's year goes without saying.
export const formatShortDate = (value) =>
    new Date(value).toLocaleDateString('en-US', {
        timeZone: TIME_ZONE,
        month: 'short',
        day: 'numeric',
    });

export const formatShortDateTime = (value) =>
    new Date(value).toLocaleString('en-US', {
        timeZone: TIME_ZONE,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });

export function StageBadge({ stage, className }) {
    return (
        <StatusPill family="candidacy" value={stage} className={className} />
    );
}

/**
 * What a failed write says. The server's message is shown as it was sent; a
 * 409 means the screen is stale, so the caller offers Reload.
 */
export function refusalOf(err, fallback) {
    return {
        message: err?.response?.data?.message || fallback,
        conflict: err?.response?.status === 409,
    };
}

export const SAVE_FALLBACK = 'That could not be saved. Try again.';

/**
 * The result of a write: a refusal in an alert (with Reload when the screen
 * is stale), and the last success in a live region that is always present so
 * a screen reader announces it.
 */
export function RefusalAlert({ refusal, onReload }) {
    return (
        <Alert variant="danger" role="alert" className="mt-2 mb-0">
            <p className="mb-0">{refusal.message}</p>
            {refusal.conflict && onReload && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    onClick={onReload}
                >
                    Reload
                </Button>
            )}
        </Alert>
    );
}

export function WriteStatus({ refusal, notice, onReload }) {
    return (
        <>
            <div role="status" className="mt-2">
                {notice}
            </div>
            {refusal && <RefusalAlert refusal={refusal} onReload={onReload} />}
        </>
    );
}
